import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { Volume } from '@/api/type-server'
import { useTeamStore } from '@/stores/team'

import { filterValidVolumes } from '../../../create-instance/utils'

// Discriminated Union for type-safe volume handling
export type TeamVolume = Volume & {
  isTeamVolume: true
  res_id: string
}

export type PersonalVolume = Volume & {
  isTeamVolume: false
  res_id?: never
}

export type EnhancedVolume = TeamVolume | PersonalVolume

export function useVolumeData(machineDomains?: number[]) {
  const [teamVolumes, setTeamVolumes] = useState<TeamVolume[]>([])
  const [personalVolumes, setPersonalVolumes] = useState<PersonalVolume[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const teamResID = useTeamStore((state) => state.teamTokenData?.teamResID)

  const loadVolumes = useCallback(async () => {
    if (!machineDomains || machineDomains.length === 0) {
      setTeamVolumes([])
      setPersonalVolumes([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      // 1. Fetch team datadrives
      if (teamResID) {
        const teamInfoRes = await API.getTeamInfo({ res_id: teamResID })
        if (teamInfoRes.team?.volumes?.subParts) {
          const teamDatadrives: TeamVolume[] = teamInfoRes.team.volumes.subParts
            .filter((vol) => vol.type === 1) // Type 1 = Datadrive
            .filter((vol) => machineDomains.includes(vol.domain))
            .map((vol) => ({
              ...vol,
              isTeamVolume: true as const, // Type assertion for literal type
            }))

          setTeamVolumes(teamDatadrives)
        } else {
          setTeamVolumes([])
        }
      } else {
        setTeamVolumes([])
      }

      // 2. Fetch personal datadrives
      const personalVolumesRes = await API.getVolumes()
      if (personalVolumesRes?.code === 0) {
        const personalVols: PersonalVolume[] = filterValidVolumes(
          personalVolumesRes.volumes || [],
          machineDomains,
        ).map((vol: Volume) => ({
          ...vol,
          isTeamVolume: false as const, // Type assertion for literal type
        }))

        setPersonalVolumes(personalVols)
      } else {
        setPersonalVolumes([])
      }
    } catch (err) {
      console.error('Failed to load volumes:', err)
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [teamResID, machineDomains])

  useEffect(() => {
    loadVolumes()
  }, [loadVolumes])

  return { teamVolumes, personalVolumes, loading, error, reload: loadVolumes }
}
